/**
 * Explore API
 *
 * Mock explore and discovery service for browse, search, and lists.
 * Provides ~30 curated views across 6 families, a Best Fit scoring
 * algorithm, separated Bucket List / Interesting lists, and
 * Dealer's Choice random challenge selection.
 */

import type {
  Challenge,
  ChallengeCategory,
  CuratedView,
  SavedItem,
  SavedListType,
  SearchSuggestion,
  DealersChoiceResult,
  ApiResponse,
  UUID,
} from '../types'
import { mockDb, delay } from './mockDb'
import { success, error } from './client'

// Session key (shared)
const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

// ============================================================================
// Helper: timestamp shorthand
// ============================================================================
const now = () => new Date().toISOString()

// ============================================================================
// ~30 Curated Views across 6 families
// ============================================================================
const CURATED_VIEWS: CuratedView[] = [
  // ── FOR_YOU (personalized) ────────────────────────────────────────────
  { id: 'view-for-you', name: 'For You', slug: 'for-you', description: 'Personalized recommendations', family: 'FOR_YOU', icon: 'heart', displayOrder: 1, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-best-fit', name: 'Best Fit', slug: 'best-fit', description: 'Multi-signal scoring based on your profile', family: 'FOR_YOU', icon: 'target', displayOrder: 2, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-streak-helper', name: 'Streak Helpers', slug: 'streak-helper', description: 'Quick challenges to keep your streak alive', family: 'FOR_YOU', icon: 'zap', displayOrder: 3, isActive: true, queryParams: { sortBy: 'duration_short' }, createdAt: now(), updatedAt: now() },
  { id: 'view-dc-history', name: "Dealer's Choice History", slug: 'dc-history', description: 'Challenges you revealed in Dealer\'s Choice', family: 'FOR_YOU', icon: 'shuffle', displayOrder: 4, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-resume', name: 'Resume Where You Left Off', slug: 'resume', description: 'Challenges you started but haven\'t finished', family: 'FOR_YOU', icon: 'play', displayOrder: 5, isActive: true, createdAt: now(), updatedAt: now() },

  // ── DISCOVER (editorial / curated) ────────────────────────────────────
  { id: 'view-featured', name: 'Featured', slug: 'featured', description: 'Staff picks', family: 'DISCOVER', icon: 'star', displayOrder: 1, isActive: true, queryParams: { isFeatured: true }, createdAt: now(), updatedAt: now() },
  { id: 'view-top-rated', name: 'Top Rated', slug: 'top-rated', description: 'Highest rated by the community', family: 'DISCOVER', icon: 'thumbs-up', displayOrder: 2, isActive: true, queryParams: { sortBy: 'rating' }, createdAt: now(), updatedAt: now() },
  { id: 'view-most-completed', name: 'Most Completed', slug: 'most-completed', description: 'The challenges everyone finishes', family: 'DISCOVER', icon: 'check-circle', displayOrder: 3, isActive: true, queryParams: { sortBy: 'completions' }, createdAt: now(), updatedAt: now() },
  { id: 'view-hidden-gems', name: 'Hidden Gems', slug: 'hidden-gems', description: 'Great challenges that flew under the radar', family: 'DISCOVER', icon: 'diamond', displayOrder: 4, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-science', name: 'Science Picks', slug: 'science-picks', description: 'Experiments, observations & STEM', family: 'DISCOVER', icon: 'beaker', displayOrder: 5, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-arts', name: 'Arts & Creative', slug: 'arts-picks', description: 'Drawing, music, crafts & more', family: 'DISCOVER', icon: 'palette', displayOrder: 6, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-cooking', name: 'Cooking & Food', slug: 'cooking-picks', description: 'Recipes, kitchen skills & food science', family: 'DISCOVER', icon: 'utensils', displayOrder: 7, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-fitness', name: 'Fitness & Movement', slug: 'fitness-picks', description: 'Sports, exercise & physical challenges', family: 'DISCOVER', icon: 'activity', displayOrder: 8, isActive: true, createdAt: now(), updatedAt: now() },

  // ── SOCIAL (community-driven) ─────────────────────────────────────────
  { id: 'view-friends-trying', name: 'Friends Are Trying', slug: 'friends-trying', description: 'Popular with people you follow', family: 'SOCIAL', icon: 'users', displayOrder: 1, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-community-popular', name: 'Community Favorites', slug: 'community-popular', description: 'Top picks across your communities', family: 'SOCIAL', icon: 'heart', displayOrder: 2, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-group-challenges', name: 'Group Challenges', slug: 'group-challenges', description: 'Better with friends', family: 'SOCIAL', icon: 'users', displayOrder: 3, isActive: true, queryParams: { sortBy: 'group' }, createdAt: now(), updatedAt: now() },
  { id: 'view-family-friendly', name: 'Family Friendly', slug: 'family-friendly', description: 'Great for the whole family', family: 'SOCIAL', icon: 'home', displayOrder: 4, isActive: true, createdAt: now(), updatedAt: now() },

  // ── FRESH (new & trending) ────────────────────────────────────────────
  { id: 'view-trending', name: 'Trending', slug: 'trending', description: 'Popular right now', family: 'FRESH', icon: 'trending-up', displayOrder: 1, isActive: true, queryParams: { sortBy: 'popular' }, createdAt: now(), updatedAt: now() },
  { id: 'view-new', name: 'New', slug: 'new', description: 'Recently added', family: 'FRESH', icon: 'plus', displayOrder: 2, isActive: true, queryParams: { sortBy: 'newest' }, createdAt: now(), updatedAt: now() },
  { id: 'view-new-this-week', name: 'New This Week', slug: 'new-this-week', description: 'Added in the last 7 days', family: 'FRESH', icon: 'clock', displayOrder: 3, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-seasonal', name: 'Seasonal', slug: 'seasonal', description: 'Perfect for the current season', family: 'FRESH', icon: 'sun', displayOrder: 4, isActive: true, createdAt: now(), updatedAt: now() },

  // ── PRACTICAL (constraint-based) ──────────────────────────────────────
  { id: 'view-free', name: 'Free Challenges', slug: 'free', description: 'No cost to participate', family: 'PRACTICAL', icon: 'gift', displayOrder: 1, isActive: true, queryParams: { isFree: true }, createdAt: now(), updatedAt: now() },
  { id: 'view-quick-wins', name: 'Quick Wins', slug: 'quick-wins', description: 'Complete in under an hour', family: 'PRACTICAL', icon: 'clock', displayOrder: 2, isActive: true, queryParams: { sortBy: 'duration_short' }, createdAt: now(), updatedAt: now() },
  { id: 'view-beginner', name: 'Beginner Friendly', slug: 'beginner-friendly', description: 'No experience needed', family: 'PRACTICAL', icon: 'star', displayOrder: 3, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-no-equipment', name: 'No Equipment Needed', slug: 'no-equipment', description: 'Just you and your curiosity', family: 'PRACTICAL', icon: 'check', displayOrder: 4, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-under-20', name: 'Under $20', slug: 'under-20', description: 'Budget-friendly challenges', family: 'PRACTICAL', icon: 'dollar-sign', displayOrder: 5, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-weekend', name: 'Weekend Warriors', slug: 'weekend-warriors', description: 'Perfect for a weekend project', family: 'PRACTICAL', icon: 'calendar', displayOrder: 6, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-indoor', name: 'Indoor Only', slug: 'indoor-only', description: 'No need to leave the house', family: 'PRACTICAL', icon: 'home', displayOrder: 7, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-outdoor', name: 'Outdoor Adventures', slug: 'outdoor-only', description: 'Get outside and explore', family: 'PRACTICAL', icon: 'compass', displayOrder: 8, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-solo', name: 'Solo Adventures', slug: 'solo-adventures', description: 'Designed for independent exploration', family: 'PRACTICAL', icon: 'user', displayOrder: 9, isActive: true, createdAt: now(), updatedAt: now() },
  { id: 'view-near-you', name: 'Near You', slug: 'near-you', description: 'Challenges close to your location', family: 'PRACTICAL', icon: 'map-pin', displayOrder: 10, isActive: true, createdAt: now(), updatedAt: now() },
]

// ============================================================================
// Interest decay
// ============================================================================

/**
 * Compute a decay factor (0..1) for quiz-based signals based on how long
 * ago the user completed onboarding.  For the first 30 days the quiz carries
 * full weight (1.0).  After that it linearly decays to a floor of 0.3 over
 * the next 60 days, meaning behavioral signals (completed challenges,
 * category activity) become dominant.
 *
 * Decay curve:
 *   days  0-30  → 1.0
 *   days 31-90  → linear 1.0 → 0.3
 *   days  91+   → 0.3 (floor — quiz data never fully disappears)
 */
function quizDecayFactor(onboardingCompletedAt?: string): number {
  if (!onboardingCompletedAt) return 1.0
  const daysSince = (Date.now() - new Date(onboardingCompletedAt).getTime()) / (1000 * 60 * 60 * 24)
  if (daysSince <= 30) return 1.0
  if (daysSince >= 90) return 0.3
  // Linear decay from 1.0 to 0.3 over days 31-90
  return 1.0 - ((daysSince - 30) / 60) * 0.7
}

// ============================================================================
// Best Fit scoring algorithm
// ============================================================================
function scoreBestFit(
  challenge: Challenge,
  userInterests: string[],
  completedCategoryIds: Set<string>,
  preferredDifficulty?: string,
  userStrengths: string[] = [],
  userAspirations: string[] = [],
  onboardingCompletedAt?: string,
): number {
  let score = 0
  const challengeCatIds = challenge.categoryIds ?? []

  // Quiz-based signals decay over time as behavioral data accumulates
  const decay = quizDecayFactor(onboardingCompletedAt)
  const behaviorBoost = 1.0 + (1.0 - decay) // inverse: grows as quiz decays

  // Interest match: base +30, decays with quiz age
  if (challengeCatIds.some((id) => userInterests.includes(id))) score += Math.round(30 * decay)

  // Strength match: base +25, decays with quiz age
  if (challengeCatIds.some((id) => userStrengths.includes(id))) score += Math.round(25 * decay)

  // Aspiration match: base +20, decays with quiz age
  if (challengeCatIds.some((id) => userAspirations.includes(id))) score += Math.round(20 * decay)

  // Aspiration + beginner difficulty: base +10, decays with quiz age
  if (
    challenge.difficulty === 'beginner' &&
    challengeCatIds.some((id) => userAspirations.includes(id))
  ) score += Math.round(10 * decay)

  // Behavioral signal: completed-category match is boosted as quiz decays
  // +20 base for novelty if NOT completed, +25 boosted if category IS completed (behavioral proof)
  if (challengeCatIds.some((id) => completedCategoryIds.has(id))) {
    score += Math.round(25 * behaviorBoost)
  } else {
    score += 20 // Novelty bonus stays constant
  }

  // Difficulty match: +15
  if (preferredDifficulty && challenge.difficulty === preferredDifficulty) score += 15

  // Recency bonus: +10 if added in last 30 days
  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000
  if (new Date(challenge.createdAt).getTime() > thirtyDaysAgo) score += 10

  // Popularity inverse: +5 for less-completed (hidden gems)
  score += Math.max(0, 5 - Math.floor(challenge.completionCount / 20))

  // Free bonus: +3
  if (challenge.isFree) score += 3

  return score
}

/**
 * Explore API
 */
export const exploreApi = {
  /**
   * Get curated views (all ~30)
   */
  async getCuratedViews(): Promise<ApiResponse<CuratedView[]>> {
    await delay(50)
    return success(CURATED_VIEWS.filter((v) => v.isActive))
  },

  /**
   * Get challenges for a curated view
   */
  async getViewChallenges(viewSlug: string, page = 1, limit = 12): Promise<ApiResponse<Challenge[]>> {
    await delay(100)

    let challenges = mockDb.getAll<Challenge>('challenges')
    const userId = getCurrentUserId()

    switch (viewSlug) {
      // ── FOR_YOU family ──────────────────────────────────────────────
      case 'for-you':
        challenges = challenges.sort(() => Math.random() - 0.5)
        break

      case 'best-fit': {
        // Multi-signal scoring with interests, strengths, aspirations, and decay
        const userStr = userId ? localStorage.getItem(SESSION_KEY) : null
        const parsed = userStr ? JSON.parse(userStr) : {}
        const userInterests: string[] = parsed.interests ?? []
        const userStrengths: string[] = parsed.strengths ?? []
        const userAspirations: string[] = parsed.aspirations ?? []
        const onboardingAt: string | undefined = parsed.onboardingCompletedAt
        const completedCategoryIds = new Set<string>()
        challenges = challenges
          .map((c) => ({ c, score: scoreBestFit(c, userInterests, completedCategoryIds, undefined, userStrengths, userAspirations, onboardingAt) }))
          .sort((a, b) => b.score - a.score)
          .map(({ c }) => c)
        break
      }

      case 'streak-helper':
        challenges = challenges
          .filter((c) => c.difficulty === 'beginner' || c.isFree)
          .sort(() => Math.random() - 0.5)
        break

      case 'dc-history':
        // Would need DC game records; for now return random sample
        challenges = challenges.sort(() => Math.random() - 0.5).slice(0, 6)
        break

      case 'resume':
        // Would check userChallenges status='in_progress'; shuffle for mock
        challenges = challenges.sort(() => Math.random() - 0.5).slice(0, 6)
        break

      // ── DISCOVER family ─────────────────────────────────────────────
      case 'featured':
        challenges = challenges.filter((c) => c.isFeatured)
        break

      case 'top-rated':
        challenges = [...challenges].sort((a, b) => (b.averageRating ?? 0) - (a.averageRating ?? 0))
        break

      case 'most-completed':
        challenges = [...challenges].sort((a, b) => b.completionCount - a.completionCount)
        break

      case 'hidden-gems':
        challenges = challenges
          .filter((c) => c.completionCount < 10 && (c.averageRating ?? 0) >= 4)
          .sort(() => Math.random() - 0.5)
        break

      case 'science-picks':
        challenges = challenges.filter((c) =>
          c.title.toLowerCase().includes('science') ||
          c.title.toLowerCase().includes('stem') ||
          c.title.toLowerCase().includes('experiment')
        )
        break

      case 'arts-picks':
        challenges = challenges.filter((c) =>
          c.title.toLowerCase().includes('art') ||
          c.title.toLowerCase().includes('draw') ||
          c.title.toLowerCase().includes('music') ||
          c.title.toLowerCase().includes('craft')
        )
        break

      case 'cooking-picks':
        challenges = challenges.filter((c) =>
          c.title.toLowerCase().includes('cook') ||
          c.title.toLowerCase().includes('recipe') ||
          c.title.toLowerCase().includes('food') ||
          c.title.toLowerCase().includes('kitchen')
        )
        break

      case 'fitness-picks':
        challenges = challenges.filter((c) =>
          c.title.toLowerCase().includes('fitness') ||
          c.title.toLowerCase().includes('sport') ||
          c.title.toLowerCase().includes('exercise') ||
          c.title.toLowerCase().includes('run')
        )
        break

      // ── SOCIAL family ───────────────────────────────────────────────
      case 'friends-trying':
      case 'community-popular':
        // Mock: shuffle (real impl would query social graph)
        challenges = challenges.sort(() => Math.random() - 0.5)
        break

      case 'group-challenges':
        challenges = challenges.filter((c) => c.soloGroup === 'group' || c.soloGroup === 'both')
        break

      case 'family-friendly':
        challenges = challenges.filter((c) => c.difficulty === 'beginner' && c.isFree)
        break

      // ── FRESH family ────────────────────────────────────────────────
      case 'trending':
        challenges = [...challenges].sort((a, b) => b.completionCount - a.completionCount)
        break

      case 'new':
        challenges = [...challenges].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        break

      case 'new-this-week': {
        const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
        challenges = challenges
          .filter((c) => new Date(c.createdAt).getTime() > weekAgo)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        break
      }

      case 'seasonal':
        // Mock: just return a shuffled subset
        challenges = challenges.sort(() => Math.random() - 0.5)
        break

      // ── PRACTICAL family ────────────────────────────────────────────
      case 'free':
        challenges = challenges.filter((c) => c.isFree)
        break

      case 'quick-wins':
        challenges = challenges.filter(
          (c) => c.estimatedDuration && (
            c.estimatedDuration.includes('30 min') ||
            c.estimatedDuration.includes('1 hour') ||
            c.estimatedDuration.includes('< 1')
          )
        )
        break

      case 'beginner-friendly':
        challenges = challenges.filter((c) => c.difficulty === 'beginner')
        break

      case 'no-equipment':
        // Mock: return free beginner challenges as proxy
        challenges = challenges.filter((c) => c.isFree && c.difficulty === 'beginner')
        break

      case 'under-20':
        challenges = challenges.filter((c) => c.isFree || (c.costAmount != null && c.costAmount <= 20))
        break

      case 'weekend-warriors':
        challenges = challenges.filter(
          (c) => c.estimatedDuration && (
            c.estimatedDuration.includes('day') ||
            c.estimatedDuration.includes('weekend')
          )
        )
        break

      case 'indoor-only':
        challenges = challenges.filter(
          (c) => c.fulfillmentType === 'digitally_guided' || c.locationType === 'indoor'
        )
        break

      case 'outdoor-only':
        challenges = challenges.filter(
          (c) => c.fulfillmentType === 'hosted' || c.locationType === 'outdoor'
        )
        break

      case 'solo-adventures':
        challenges = challenges.filter((c) => c.soloGroup === 'solo' || c.soloGroup === 'both')
        break

      case 'near-you':
        // Mock: return shuffled (real impl would use geolocation)
        challenges = challenges.sort(() => Math.random() - 0.5)
        break

      default:
        // Category-based slugs (e.g., /explore/category/outdoor)
        if (viewSlug.startsWith('category/')) {
          const catSlug = viewSlug.replace('category/', '')
          const categories = mockDb.getAll<ChallengeCategory>('categories')
          const matchCat = categories.find((c) => c.slug === catSlug)
          if (matchCat) {
            challenges = challenges.filter((c) => c.categoryIds?.includes(matchCat.id))
          }
        }
        break
    }

    // If filter returned nothing, fall back to full list for mock demo
    if (challenges.length === 0) {
      challenges = mockDb.getAll<Challenge>('challenges').sort(() => Math.random() - 0.5)
    }

    // Paginate
    const start = (page - 1) * limit
    const paginatedChallenges = challenges.slice(start, start + limit)

    return success(paginatedChallenges)
  },

  /**
   * Get Best Fit challenges with scores (for debugging / advanced display)
   */
  async getBestFitChallenges(
    userInterests: string[] = [],
    preferredDifficulty?: string,
    userStrengths: string[] = [],
    userAspirations: string[] = [],
    onboardingCompletedAt?: string,
  ): Promise<ApiResponse<Challenge[]>> {
    await delay(80)

    const challenges = mockDb.getAll<Challenge>('challenges')
    const completedCategoryIds = new Set<string>()

    const scored = challenges
      .map((c) => ({
        challenge: c,
        score: scoreBestFit(c, userInterests, completedCategoryIds, preferredDifficulty, userStrengths, userAspirations, onboardingCompletedAt),
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 20)
      .map(({ challenge }) => challenge)

    return success(scored)
  },

  /**
   * Search with autocomplete suggestions
   */
  async getSearchSuggestions(query: string): Promise<ApiResponse<SearchSuggestion[]>> {
    await delay(30)

    if (!query || query.length < 2) {
      return success([])
    }

    const lowerQuery = query.toLowerCase()
    const suggestions: SearchSuggestion[] = []

    // Search challenges
    const challenges = mockDb.getAll<Challenge>('challenges')
    const matchingChallenges = challenges
      .filter((c) => c.title.toLowerCase().includes(lowerQuery))
      .slice(0, 3)

    matchingChallenges.forEach((c) => {
      suggestions.push({
        type: 'challenge',
        text: c.title,
        id: c.id,
      })
    })

    // Search categories
    const categories = mockDb.getAll<ChallengeCategory>('categories')
    const matchingCategories = categories
      .filter((c) => c.name.toLowerCase().includes(lowerQuery))
      .slice(0, 2)

    matchingCategories.forEach((c) => {
      suggestions.push({
        type: 'category',
        text: c.name,
        id: c.id,
      })
    })

    return success(suggestions)
  },

  // ============================================================================
  // Saved Lists (Bucket List & Interesting)
  // ============================================================================

  /**
   * Get user's saved list, optionally filtered by list type
   */
  async getSavedList(listType?: SavedListType): Promise<ApiResponse<SavedItem[]>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    let items = mockDb.findWhere<SavedItem>('savedList', (item) => item.userId === userId)

    if (listType) {
      items = items.filter((item) => item.listType === listType)
    }

    items.sort((a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime())

    // Populate challenges
    const populatedItems = items.map((item) => ({
      ...item,
      challenge: mockDb.findById<Challenge>('challenges', item.challengeId) ?? undefined,
    }))

    return success(populatedItems)
  },

  /**
   * Add challenge to saved list
   */
  async addToSavedList(
    challengeId: UUID,
    listType: SavedListType = 'bucket_list',
    notes?: string
  ): Promise<ApiResponse<SavedItem>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Check if already saved (in either list)
    const existing = mockDb.findOne<SavedItem>(
      'savedList',
      (item) => item.userId === userId && item.challengeId === challengeId
    )

    if (existing) {
      // If same list, error; if different list, move it
      if (existing.listType === listType) {
        return error('Challenge already saved')
      }
      // Move to the other list
      const updated = mockDb.update<SavedItem>('savedList', existing.id, { listType })
      if (updated) {
        updated.challenge = mockDb.findById<Challenge>('challenges', updated.challengeId) ?? undefined
        return success(updated)
      }
      return error('Failed to move item')
    }

    const item = mockDb.insert<SavedItem>('savedList', {
      userId,
      challengeId,
      addedAt: new Date().toISOString(),
      notes,
      listType,
    })

    item.challenge = mockDb.findById<Challenge>('challenges', challengeId) ?? undefined

    return success(item)
  },

  /**
   * Move item between lists
   */
  async moveToList(itemId: UUID, listType: SavedListType): Promise<ApiResponse<SavedItem>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const item = mockDb.findById<SavedItem>('savedList', itemId)
    if (!item || item.userId !== userId) {
      return error('Item not found')
    }

    const updated = mockDb.update<SavedItem>('savedList', itemId, { listType })
    if (!updated) {
      return error('Failed to move')
    }

    updated.challenge = mockDb.findById<Challenge>('challenges', updated.challengeId) ?? undefined
    return success(updated)
  },

  /**
   * Remove from saved list
   */
  async removeFromSavedList(challengeId: UUID): Promise<ApiResponse<void>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const item = mockDb.findOne<SavedItem>(
      'savedList',
      (i) => i.userId === userId && i.challengeId === challengeId
    )

    if (!item) {
      return error('Item not found')
    }

    mockDb.delete('savedList', item.id)
    return success(undefined)
  },

  /**
   * Update saved list item
   */
  async updateSavedItem(
    itemId: UUID,
    data: { notes?: string; priority?: 'high' | 'medium' | 'low' | null }
  ): Promise<ApiResponse<SavedItem>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const item = mockDb.findById<SavedItem>('savedList', itemId)
    if (!item || item.userId !== userId) {
      return error('Item not found')
    }

    // Handle clearing priority (null → undefined on the stored object)
    const updateData: Partial<SavedItem> = {}
    if (data.notes !== undefined) updateData.notes = data.notes
    if (data.priority === null) {
      updateData.priority = undefined
    } else if (data.priority !== undefined) {
      updateData.priority = data.priority
    }

    const updated = mockDb.update<SavedItem>('savedList', itemId, updateData)
    if (!updated) {
      return error('Failed to update')
    }
    // Explicitly clear priority if null was passed (spread won't clear undefined)
    if (data.priority === null) {
      delete (updated as Record<string, unknown>).priority
    }

    updated.challenge = mockDb.findById<Challenge>('challenges', updated.challengeId) ?? undefined
    return success(updated)
  },

  /**
   * Check if challenge is saved
   */
  async isSaved(challengeId: UUID): Promise<ApiResponse<boolean>> {
    await delay(20)

    const userId = getCurrentUserId()
    if (!userId) {
      return success(false)
    }

    const item = mockDb.findOne<SavedItem>(
      'savedList',
      (i) => i.userId === userId && i.challengeId === challengeId
    )

    return success(!!item)
  },

  // ============================================================================
  // Dealer's Choice
  // ============================================================================

  /**
   * Get dealer's choice challenge (random with multiplier)
   */
  async getDealersChoice(): Promise<ApiResponse<DealersChoiceResult>> {
    await delay(200)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Get all available challenges
    const challenges = mockDb.getAll<Challenge>('challenges')

    if (challenges.length === 0) {
      return error('No challenges available')
    }

    // Pick random challenge
    const randomIndex = Math.floor(Math.random() * challenges.length)
    const challenge = challenges[randomIndex]

    // Generate multiplier (1.5x to 3x)
    const multiplier = Math.round((1.5 + Math.random() * 1.5) * 10) / 10

    // Expires in 24 hours
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()

    return success({
      challenge,
      multiplier,
      expiresAt,
      accepted: false,
    })
  },

  /**
   * Accept dealer's choice (start the challenge with multiplier)
   */
  async acceptDealersChoice(challengeId: UUID, multiplier: number): Promise<ApiResponse<boolean>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Note: In a real implementation, this would create a UserChallenge
    // with the dcMultiplier set
    console.log(`Accepted Dealer's Choice: ${challengeId} with ${multiplier}x multiplier`)

    return success(true)
  },
}
